<script lang="ts" setup>
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import SealsHandler from "@/views/trips/components/SealsHandler.vue";
import { useError } from "@/composables/useError";
import { useSeals } from "@/composables/useSeals";
import { DialogSize } from "@/constants/dialog";
import SealValidator from "@/validations/seal.validator";
import type { VForm } from "vuetify/components";
import type { RouteStopDto, TravelOrderDto } from "@/api/trips/dto/trip.dto";
import type { StopInfoSeal } from "@/types/trip";

const { t } = useI18n();
const { scrollToError } = useError();
const { getTravelOrdersSeals, saveSeals } = useSeals();

const model = defineModel<boolean>({ required: true });

const props = defineProps<{
  tripId: number;
  travelOrders: TravelOrderDto[];
  routeStops: RouteStopDto[];
}>();

const emit = defineEmits<{
  change: [];
}>();

const formRef = useTemplateRef<VForm | null>("form");

const seals = ref<StopInfoSeal[]>(getTravelOrdersSeals(props.travelOrders));
const initialSeals = ref<StopInfoSeal[]>(seals.value.map((seal) => ({ ...seal })));
const isSealsValidated = ref<boolean>(false);
const isSaving = ref<boolean>(false);

const sealErrors = computed<Record<string, string | null>>(() => {
  if (!isSealsValidated.value) {
    return {};
  }

  return new SealValidator(seals.value).validate();
});

const hasSealErrors = computed<boolean>(() => {
  return Object.values(sealErrors.value).some(Boolean);
});

function onSealDeleted(deletedSeal: StopInfoSeal): void {
  initialSeals.value = initialSeals.value.filter((seal) => seal.id !== deletedSeal.id);
  emit("change");
}

async function save(): Promise<void> {
  isSealsValidated.value = true;

  const result = await formRef.value?.validate();

  if (!result?.valid || hasSealErrors.value) {
    scrollToError(formRef.value?.$el);

    return;
  }

  isSaving.value = true;

  const saveResult = await saveSeals(props.tripId, seals.value, initialSeals.value);

  seals.value = saveResult.seals;
  initialSeals.value = saveResult.initialSeals.map((seal) => ({ ...seal }));

  isSaving.value = false;

  if (!saveResult.isSaved) {
    return;
  }

  emit("change");
  model.value = false;
}
</script>

<template>
  <omni-dialog
    v-model="model"
    :primary-action-label="t('general.save')"
    :primary-button-loading="isSaving"
    :secondary-action-label="t('general.cancel')"
    :secondary-button-disabled="isSaving"
    :size="DialogSize.normal"
    :title="t('trip.seals-management')"
    data-qa="seals-handler-dialog"
    @primary-action:click="save"
    @secondary-action:click="model = false"
  >
    <v-form ref="form">
      <seals-handler
        v-model="seals"
        :errors="sealErrors"
        :route-stops="props.routeStops"
        hide-title
        :travel-orders="props.travelOrders"
        :trip-id="props.tripId"
        @deleted="onSealDeleted"
      />
    </v-form>
  </omni-dialog>
</template>

<style lang="scss" scoped></style>
